"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  type User,
  type IdentityConfiguration,
  getIdentityConfiguration,
  apiGetProfile,
  apiLogin,
  apiLogout,
  apiRegister,
  apiVerifyOtp,
  apiResendOtp,
  apiVerifyTwoFactor,
  apiForgotPassword,
  apiResetPassword,
  clearCachedUser,
  getCachedUser,
  setCachedUser,
  getSessionStatus,
  recordSessionActivity,
} from "@/lib/api";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { watchSessionActivity } from "@/lib/session-activity";

interface AuthContextType {
  user: User | null;
  identityConfiguration: IdentityConfiguration | null;
  reloadIdentityConfiguration: () => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
  // Phase 6 of [[rbac-permission-overhaul-initiative]]: derived from
  // user.capabilities (populated by /profile/me — see the User type in
  // lib/api.ts). This is a UX layer only, same as the plan's invariant #11
  // states outright — the backend's require_permission(...) is the only
  // real enforcement; hiding a button here never substitutes for that.
  hasCapability: (key: string) => boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; requires2FA?: boolean; tempToken?: string; email?: string }>;
  refreshUser: (policy?: "required" | "optional") => Promise<void>;
  register: (payload: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
  }) => Promise<{ success: boolean; email?: string }>;
  verifyOtp: (email: string, code: string) => Promise<boolean>;
  resendOtp: (email: string) => Promise<boolean>;
  verifyTwoFactor: (email: string, code: string, tempToken: string) => Promise<boolean>;
  forgotPassword: (email: string) => Promise<boolean>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const t = useTranslations("auth");
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [identityConfiguration,setIdentityConfiguration]=useState<IdentityConfiguration|null>(null);
  const reloadIdentityConfiguration=useCallback(async()=>{
    setIdentityConfiguration(null);
    try {setIdentityConfiguration(await getIdentityConfiguration());}
    catch {setIdentityConfiguration({enterprise:false,local_credentials:false,connection_required:true,mfa_required:true});}
  },[]);
  useEffect(()=>{void reloadIdentityConfiguration();},[reloadIdentityConfiguration]);
  const expireSession = useCallback((unavailable = false) => {
    clearCachedUser(); setUser(null);
    toast.error(t(unavailable ? "sessionUnavailable" : "sessionExpired"));
    // A failed/aged probe belongs to this view. Only explicit user logout
    // revokes the shared server session used by other tabs.
    window.location.href = "/?login_required=1";
  }, [t]);

  useEffect(() => {
    if (!user?.id || isLoading) return;
    return watchSessionActivity({ getStatus: getSessionStatus, signalActivity: recordSessionActivity,
      onExpired: () => expireSession(), onUnavailable: () => expireSession(true) });
  }, [user?.id, isLoading, expireSession]);

  const refreshUser = useCallback(async (policy: "required" | "optional" = "required") => {
    // No client-visible way to check "is there a session" before asking —
    // the access_token cookie is HttpOnly (Phase 2), so this always has to
    // just ask the server and treat a failure as logged-out. The mount
    // effect below passes "optional": a 401 there is the ordinary state
    // for a not-yet-logged-in visitor, not a session error, and must not
    // trigger a forced redirect (which previously bounced every anonymous
    // page load through "/?login_required=1").
    const { ok, data } = await apiGetProfile(policy);
    if (ok && data) {
      if (policy === "required") {
        const current = new URL(window.location.href);
        current.searchParams.delete("login_required");
        window.history.replaceState(null, "", current.pathname + current.search + current.hash);
      }
      setUser(data);
      setCachedUser(data);
    } else {
      setUser(null);
      clearCachedUser();
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    // An automatic expiry/unavailable redirect stays at login until an
    // explicit sign-in succeeds, even if another tab has a valid cookie.
    if (new URLSearchParams(window.location.search).get("login_required") === "1") {
      clearCachedUser(); setUser(null); setIsLoading(false);
      return;
    }
    // First, load cached user for immediate UI
    const cached = getCachedUser();
    if (cached) {
      setUser(cached);
    }
    // Then refresh from server — "optional" so a logged-out visitor's
    // inevitable 401 here doesn't force a redirect.
    refreshUser("optional");
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const { ok, data } = await apiLogin(email, password);

    if (ok && data.requires_2fa && data.temp_token) {
      return { success: false, requires2FA: true, tempToken: data.temp_token, email };
    }

    if (ok && data.authenticated) {
      // Session cookies are already set by the server response itself —
      // nothing to store here (Phase 2).
      await refreshUser();
      toast.success(`Welcome back${user ? ", " + user.first_name : ""}!`);
      return { success: true };
    }

    const message = (data as any)?.error?.message || (data as any)?.message || (data as any)?.detail || "Login failed";
    toast.error(message);
    return { success: false };
  }, [refreshUser, user]);

  const register = useCallback(async (payload: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
  }) => {
    const { ok, data } = await apiRegister(payload);

    if (ok) {
      toast.success("Account created! Please verify your email.");
      return { success: true, email: payload.email };
    }

    const message = (data as any)?.error?.message || (data as any)?.message || (data as any)?.detail || "Registration failed";
    toast.error(message);
    return { success: false };
  }, []);

  const verifyOtp = useCallback(async (email: string, code: string) => {
    const { ok, data } = await apiVerifyOtp(email, code);

    if (ok) {
      toast.success("Email verified! Please log in.");
      return true;
    }

    const message = (data as any)?.error?.message || (data as any)?.message || (data as any)?.detail || "Invalid or expired code";
    toast.error(message);
    return false;
  }, []);

  const resendOtp = useCallback(async (email: string) => {
    const { ok, data } = await apiResendOtp(email);

    if (ok) {
      toast.success("New code sent to your email.");
      return true;
    }

    const message = (data as any)?.error?.message || (data as any)?.message || (data as any)?.detail || "Failed to resend code";
    toast.error(message);
    return false;
  }, []);

  const verifyTwoFactor = useCallback(async (email: string, code: string, tempToken: string) => {
    const { ok, data } = await apiVerifyTwoFactor(email, code, tempToken);

    if (ok && data.authenticated) {
      await refreshUser();
      toast.success("Welcome back!");
      return true;
    }

    const message = (data as any)?.error?.message || (data as any)?.message || (data as any)?.detail || "Invalid or expired code";
    toast.error(message);
    return false;
  }, [refreshUser]);

  const forgotPassword = useCallback(async (email: string) => {
    const { ok, data } = await apiForgotPassword(email);

    if (ok) {
      toast.success("Reset code sent to your email.");
      return true;
    }

    const message = (data as any)?.error?.message || (data as any)?.message || (data as any)?.detail || "Failed to send reset code";
    toast.error(message);
    return false;
  }, []);

  const resetPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    const { ok, data } = await apiResetPassword(email, code, newPassword);

    if (ok) {
      toast.success("Password reset successfully! Please log in.");
      return true;
    }

    const message = (data as any)?.error?.message || (data as any)?.message || (data as any)?.detail || "Failed to reset password";
    toast.error(message);
    return false;
  }, []);

  const hasCapability = useCallback(
    (key: string) => Boolean(user?.capabilities?.includes(key)),
    [user]
  );

  const logout = useCallback(async () => {
    // apiLogout() blacklists the access token server-side and clears the
    // session cookies via Set-Cookie in the response — nothing left for the
    // client to remove except the cached (non-sensitive) profile display data.
    await apiLogout();
    clearCachedUser();
    setUser(null);
    toast.success("Signed out successfully");
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        identityConfiguration,
        reloadIdentityConfiguration,
        isLoading,
        isAuthenticated: !!user,
        hasCapability,
        login,
        register,
        verifyOtp,
        resendOtp,
        verifyTwoFactor,
        forgotPassword,
        resetPassword,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/** Convenience wrapper named to match the plan's literal spec
 * (`useHasCapability(key)`) — identical to `useAuth().hasCapability(key)`,
 * kept as its own hook so call sites don't have to pull the whole auth
 * context in just to ask one yes/no question. */
export function useHasCapability(key: string): boolean {
  return useAuth().hasCapability(key);
}
